import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { KbCategoryFilter } from "@/components/KbCategoryFilter";

/**
 * Move a category without dragging. The tree is hundreds of rows deep, so
 * dragging across a scrolling page is hopeless — pick the new parent and where
 * it lands among its siblings instead.
 */
export function KbMoveCategoryDialog({ open, onOpenChange, category, cats, onMoved }) {
  const [parent, setParent] = useState("all");
  const [where, setWhere] = useState("last");
  const [after, setAfter] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !category) return;
    setParent(category.parent_id || "all");
    setWhere("last");
    setAfter("");
  }, [open, category]);

  // Its own subtree can't be its new home.
  const banned = useMemo(() => {
    if (!category) return [];
    const out = [category.cat_id];
    const walk = (pid) => (cats || []).filter((c) => c.parent_id === pid)
      .forEach((c) => { out.push(c.cat_id); walk(c.cat_id); });
    walk(category.cat_id);
    return out;
  }, [category, cats]);

  const siblings = useMemo(() => (cats || [])
    .filter((c) => (c.parent_id || null) === (parent === "all" ? null : parent)
      && c.cat_id !== category?.cat_id)
    .sort((a, b) => (a.position || 0) - (b.position || 0)), [cats, parent, category]);

  const save = async () => {
    if (!category) return;
    const newParent = parent === "all" ? null : parent;
    const list = [...siblings];
    const idx = where === "first" ? 0
      : where === "after" ? list.findIndex((c) => c.cat_id === after) + 1
        : list.length;
    list.splice(where === "after" && !after ? list.length : idx, 0, category);
    setSaving(true);
    try {
      await api.post("/kb/categories/reorder", {
        items: list.map((c, i) => ({ cat_id: c.cat_id, position: i + 1, parent_id: newParent })),
      });
      toast.success(`Moved “${(category.name || "").trim()}”`);
      onMoved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="kb-move-cat-dialog">
        <DialogHeader>
          <DialogTitle>Move “{(category?.name || "").trim()}”</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">New parent</Label>
            <KbCategoryFilter tree={cats || []} value={parent} single exclude={banned}
              allLabel="— Top level —" onChange={setParent} testid="kb-move-cat-parent" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Position</Label>
            <Select value={where} onValueChange={setWhere}>
              <SelectTrigger data-testid="kb-move-cat-where"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="first">First</SelectItem>
                <SelectItem value="last">Last</SelectItem>
                <SelectItem value="after" disabled={!siblings.length}>After a category…</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {where === "after" && (
            <div className="space-y-1.5">
              <Label className="text-xs">After</Label>
              <Select value={after} onValueChange={setAfter}>
                <SelectTrigger data-testid="kb-move-cat-after">
                  <SelectValue placeholder="Pick a category" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {siblings.map((c) => (
                    <SelectItem key={c.cat_id} value={c.cat_id}>{(c.name || "").trim()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <p className="text-xs text-slate-400 dark:text-slate-500">
            {siblings.length
              ? `${siblings.length} categor${siblings.length === 1 ? "y" : "ies"} already sit there.`
              : "Nothing else sits there yet."}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="kb-move-cat-save"
            className="bg-primary text-primary-foreground hover:bg-primary/90">
            {saving ? "Moving…" : "Move"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
